import Head from "next/head";
import Link from "next/link";
import Layout from "components/includes/Layout";
import { useContext, useEffect, useState } from "react";
import { createRoot } from "react-dom";
import { connect } from "react-redux";
import { useRouter } from "next/router";
import { logout } from "src/redux/action/auth";
import { I18nContext } from "components/I18N";
import Banner from "components/commons/Banner";
import BlogIndex from "components/commons/BlogIndex";
import BlogLayout from "components/includes/BlogLayout";
import { MOCK_BLOG, MOCK_BLOG_HTML } from "configs/mockData";
import DateNameDisplay from "components/blog/DateNameDisplay";
import BlogSection from "components/blog/BlogSection";
import { API, graphqlOperation } from "aws-amplify";
import { blogsByUrl, searchBlogs } from "src/graphql/queries";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { useDynamicCanonicalUrl } from "../../hooks/useDynamicCanonicalUrl";
import CallToAction from "../../components/blog/CallToAction";

function Meta({ title, description, image, url }) {
  //hooks
  const { canonicalUrl, enCanonicalUrl, thCanonicalUrl, addMetaNoIndex } =
    useDynamicCanonicalUrl();

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} data-react-helmet="true" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="image" content={image} data-react-helmet="true" />
      <meta
        property="og:type"
        name="type"
        content="article"
        data-react-helmet="true"
      />
      <meta
        property="og:url"
        name="url"
        content={canonicalUrl}
        data-react-helmet="true"
      />
      <meta
        property="og:title"
        name="title"
        content={title}
        data-react-helmet="true"
      />
      <meta
        property="og:description"
        name="description"
        content={description}
        data-react-helmet="true"
      />
      <meta
        property="og:image"
        name="image"
        content={image}
        data-react-helmet="true"
      />
      <meta
        name="twitter:card"
        content="summary_large_image"
        data-react-helmet="true"
      />
      <meta name="twitter:title" content={title} data-react-helmet="true" />
      <meta
        name="twitter:description"
        content={description}
        data-react-helmet="true"
      />
      <meta name="twitter:image" content={image} data-react-helmet="true" />
      <meta
        property="fb:app_id"
        content="560604011641316"
        data-react-helmet="true"
      />
      <meta
        name="google-site-verification"
        content="aoDV0F-xPmfvnmFWVuVzWGRDB5C3rueQIrRoiRegRq8"
      />
      <meta
        name="facebook-domain-verification"
        content="dut2pbpprooie10vv5oooohpu8ziyj"
      />
      {addMetaNoIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" href={thCanonicalUrl} hrefLang="th" />
      <link rel="alternate" href={enCanonicalUrl} hrefLang="en" />
      <link rel="icon" type="image/icon" href="/favicon.ico" />
      <link rel="shortcut icon" type="image/icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="57x57" href="/apple-icon-57x57.png" />
      <link rel="apple-touch-icon" sizes="60x60" href="/apple-icon-60x60.png" />
      <link rel="apple-touch-icon" sizes="72x72" href="/apple-icon-72x72.png" />
      <link rel="apple-touch-icon" sizes="76x76" href="/apple-icon-76x76.png" />
      <link
        rel="apple-touch-icon"
        sizes="114x114"
        href="/apple-icon-114x114.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="120x120"
        href="/apple-icon-120x120.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="144x144"
        href="/apple-icon-144x144.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="152x152"
        href="/apple-icon-152x152.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-icon-180x180.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="192x192"
        href="/android-icon-192x192.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="48x48"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="96x96"
        href="/favicon-96x96.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@300;400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="msapplication-TileImage" content="/ms-icon-144x144.png" />
      <meta name="theme-color" content="#ffffff"></meta>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '1616519548698641');
            fbq('track', 'PageView');
            `,
        }}
      />
      <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=G-C4LRHNLK74"
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-KGBLG33');
            `,
        }}
      />
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-KGBLG33"
          height="0"
          width="0"
          style="display:none;visibility:hidden"
        ></iframe>
      </noscript>
      <noscript>
        <img
          height="1"
          width="1"
          style="display:none"
          src="https://www.facebook.com/tr?id=1616519548698641&ev=PageView&noscript=1"
        />
      </noscript>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '400318178561566');
            fbq('track', 'PageView');
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style="display:none"
          src="https://www.facebook.com/tr?id=400318178561566&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
}

const Page = ({ auth }) => {
  function removeHtmlTags(htmlString) {
    if (htmlString) {
      return htmlString.replace(/<[^>]*>/g, "");
    }
    return;
  }
  //hooks
  const { I18n, langCode } = useContext(I18nContext);
  const router = useRouter();

  //states
  const [item, setItem] = useState(false);
  const [list, setList] = useState([]);
  const url = router.query.id;

  //fetch data
  const getData = async () => {
    const result = await API.graphql({
      query: blogsByUrl,
      variables: { url },
      authMode: auth.user ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY",
    });
    let i = get(result, "data.blogsByUrl.items.0", false);
    i.description = i.description.replace(
      "[category_block]",
      `<div id="category-block-container"><div id="category-block-placeholder"></div>`
    );
    i.subDescription = i.subDescription.replace(
      "[category_block]",
      `<div id="category-block-container"><div id="category-block-placeholder"></div>`
    );
    i.descriptionEN = i.descriptionEN.replace(
      "[category_block]",
      `<div id="category-block-container"><div id="category-block-placeholder"></div>`
    );
    i.subDescriptionEN = i.subDescriptionEN.replace(
      "[category_block]",
      `<div id="category-block-container"><div id="category-block-placeholder"></div>`
    );
    setItem(i);
    const blogsResult = await API.graphql({
      query: searchBlogs,
      variables: {
        limit: 3,
        filter: {
          status: { eq: "PUBLISHED" },
          blogCategoryID: {
            eq: get(result, "data.blogsByUrl.items.0.blogCategoryID", false),
          },
        },
        sort: { direction: "desc", field: "updatedAt" },
      },
      authMode: auth.user ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY",
    });
    setList([...get(blogsResult, ["data", "searchBlogs", "items"], [])]);
  };

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    getData();
  }, [router]);

  useEffect(() => {
    // temporary render target
    var temp = document.createElement("div");
    // render
    const root = createRoot(temp);
    root.render(<CallToAction item={item} router={router} />);
    // grab the container
    var container = document.getElementById("category-block-container");
    var placeholder = document.getElementById("category-block-placeholder");
    // and replace the child
    if (container && placeholder) {
      container.replaceChild(
        temp,
        document.getElementById("category-block-placeholder")
      );
    }
  }, [item]);

  if (!item) {
    return <></>;
  }

  return (
    <Layout
      header
      headerFullwidth
      meta={
        <Meta
          title={get(item, "seoTitle", "")}
          description={get(item, "seoDescription", "")}
          image={
            get(item, "image.key", false)
              ? `https://${get(
                  item,
                  "image.bucket",
                  ""
                )}.s3.ap-southeast-1.amazonaws.com/public/${encodeURIComponent(
                  get(item, "image.key", "")
                )}`
              : "https://sayhibro5d98f21dfb374d31b878033166abec6d182612-prod.s3.ap-southeast-1.amazonaws.com/seoImage.jpg"
          }
          url={url}
        />
      }
    >
      <div className="flex flex-col gap-[48px] md:gap-[80px]">
        <div className="w-full bg-[#F7F5F1] py-[80px] flex flex-col items-center text-center px-[24px] md:px-0">
          <p className="mb-[16px]">{I18n.get("blogPageTitle")}</p>
          <h3
            className="detail-blog-title font-bold px-[17px] lg:px-[40px]"
            dangerouslySetInnerHTML={{
              __html:
                langCode === "en" && !isEmpty(item.titleEN)
                  ? removeHtmlTags(item.titleEN)
                  : removeHtmlTags(item.title),
            }}
          />
          <DateNameDisplay
            center
            authorName={item.authorName}
            reviewedBy={item.reviewedBy}
            createdAt={item.createdAt}
          />
        </div>

        <BlogLayout>
          <div className="flex-1 flex flex-col gap-12 overflow-hidden article">
            <style jsx global>
              {`
                @media screen and (min-width: 992px) {
                  .article > div:not(:last-of-type) {
                    width: 904px !important;
                    margin: auto !important;
                  }
                }
                .article img {
                  width: auto !important;
                  border-radius: 15px;
                  margin-bottom: 20px;
                }
              `}
            </style>
            <div
              dangerouslySetInnerHTML={{
                __html:
                  langCode === "en" && !isEmpty(item.descriptionEN)
                    ? item.descriptionEN
                    : item.description,
              }}
            />
            <div
              dangerouslySetInnerHTML={{
                __html:
                  langCode === "en" && !isEmpty(item.subDescriptionEN)
                    ? item.subDescriptionEN
                    : item.subDescription,
              }}
            />
            <hr />
            <div className="flex justify-between bottomShareRib">
              <div className="flex gap-2">
                <div className="tag-name">{item.blogCategory?.name}</div>
              </div>
              <div className="flex gap-1">
              </div>
            </div>
          </div>
        </BlogLayout>

        <Banner backgroundImage={"/images/about/footer.png"} footer>
          <p className="heading5 font-[CentraNo2] mb-4">
            {I18n.get("readyToStartOnlineVisit")}
          </p>
          <p
            className="font-[CentraNo2]"
            dangerouslySetInnerHTML={{
              __html: I18n.get("aboutPageSubtitle"),
            }}
          />
          <Link passHref href="/treatment">
            <button className="big light">
              <p className="title text-[#060606]">{I18n.get("startConsult")}</p>
            </button>
          </Link>
        </Banner>
        {/** @desc Related Article */}
        <div className="container-wrapper pb-[20px]">
          <p className="heading6 font-bold mb-6 font-[CentraNo2]">
            {I18n.get("relatedArticle")}
          </p>
          <BlogSection items={list} />
        </div>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(Page);
